import * as React from "react"
import type { Kind } from "../lib/client"
import { languageName, type DocEntry } from "../lib/docs"
import { crumbs, prettify, type Node } from "../lib/tree"
import { Icon } from "./Icon"
import { New } from "./New"

/**
 * A grid of things, and the one card that makes a new one.
 *
 * **Cards rather than a table**, because the level below a section is a choice between a
 * handful of things and a table optimises for scanning a hundred. Each card carries the
 * document's own first paragraph — read from the document, never stored beside it, so it
 * cannot describe something the document stopped saying.
 */
export const CardGrid: React.FC<{
  nodes: Node[]
  lang: string
  onOpen: (path: string) => void
  onEnter: (dir: string) => void
  /** The last card creates. Omitted where creating makes no sense — see `Home`. */
  create?: { kinds: Kind[]; onCreate: (k: string, n: string, p: string | null) => Promise<void> }
}> = ({ nodes, lang, onOpen, onEnter, create }) => (
  <div className="sf-cards">
    {nodes.map(n => (
      <button key={n.path} className={`sf-card is-${n.kind}`}
              onClick={() => (n.dir ? onEnter(n.path) : onOpen(n.entry
                ? (n.entry.variants?.[lang]?.path ?? n.entry.path) : n.path))}>
        <span className="sf-card-kind">
          <Icon kind={n.kind} />{n.kind}
        </span>
        <b className="sf-card-title">{n.title}</b>
        {n.summary && <span className="sf-card-summary">{n.summary}</span>}
        {/* What a table column used to carry, kept on the card: a document's own declared
            state is the thing people scan a list for. */}
        {!!badges(n.meta).length && (
          <span className="sf-card-badges">
            {badges(n.meta).map(b => <span key={b} className="sf-badge">{b}</span>)}
          </span>
        )}
        <span className="sf-card-foot">
          {n.dir
            ? `${n.count} ${n.count === 1 ? "item" : "items"}`
            : Object.keys(n.entry?.variants ?? {}).map(languageName).join(" · ")}
          {!n.dir && n.entry && !n.entry.variants?.[lang] && (
            <span className="sf-untranslated"
                  title={`not translated to ${languageName(lang)} yet`}> —</span>
          )}
        </span>
      </button>
    ))}

    {create && (
      /* Last, and shaped differently: it is the one card that is not a thing that exists. */
      <div className="sf-card is-new">
        <New kinds={create.kinds} onCreate={create.onCreate} label="+ Create" />
      </div>
    )}
  </div>
)

/**
 * A section's own page: what it is, what is in it, and a way to add to it.
 *
 * Drilling in is this same page again, one level down, which is what makes the depth
 * unlimited without a second component.
 */
export const Cards: React.FC<{
  title: string
  summary: string
  kicker: string
  nodes: Node[]
  indexEntry?: DocEntry
  lang: string
  kinds: Kind[]
  path: string
  onOpen: (path: string) => void
  onEnter: (dir: string) => void
  onCreate: (kind: string, name: string, itemParent: string | null) => Promise<void>
}> = ({ title, summary, kicker, nodes, indexEntry, lang, kinds, path, onOpen, onEnter,
        onCreate }) => (
  <>
    <header className="sf-doc-head">
      {!!path && (
        <nav className="sf-crumbs" aria-label="Where you are">
          {crumbs(path).slice(0, -1).map(c => (
            <button key={c} className="sf-crumb" onClick={() => onEnter(c)}>
              {prettify(c.split("/").pop() ?? c)}
            </button>
          ))}
        </nav>
      )}
      <p className="sf-kicker">{kicker}</p>
      <h1>{title}</h1>
      {summary && <p className="sf-lede">{summary}</p>}
      <div className="sf-meta">
        <span className="sf-chip">
          <b>{nodes.length}</b> {nodes.length === 1 ? "thing" : "things"}
        </span>
        {indexEntry && (
          <button className="sf-ghost" onClick={() => onOpen(indexEntry.path)}>
            read this section's page
          </button>
        )}
      </div>
    </header>

    <CardGrid nodes={nodes} lang={lang} onOpen={onOpen} onEnter={onEnter}
              create={{ kinds, onCreate }} />

    {!nodes.length && (
      <p className="sf-note">
        <b>Nothing here yet.</b>
        Everything filed here will appear as a card. Use <b>+ Create</b> to start one.
      </p>
    )}
  </>
)

/** Frontmatter worth showing without opening the document, in the order it is worth it. */
const BADGED = ["status", "state", "version", "last_reviewed"]

function badges(meta?: Record<string, string>): string[] {
  if (!meta) return []
  return BADGED.filter(k => meta[k]).map(k => meta[k])
}
